import React from "react";
import { FaCheck } from "react-icons/fa6";
import moment from "moment";
import type { Draft } from "../../queries/drafts.queries";
import type { InvoiceFormValues } from "../invoice-form/invoice-form.types";
import { fromAdToBs } from "../../global/function";
import { capitalizeWords, highlightMatch } from "./highlight-match.util";

// A draft-list row is either a server-fetched Draft or a locally-created
// entry (a fresh dummy draft, or an in-progress unsaved edit) — both are
// duck-typed subsets of the same fields, never a strict Draft.
export type DraftListEntry = Partial<Draft> & Record<string, unknown>;

interface DraftItemProps {
	draft: DraftListEntry;
	isSelected: boolean;
	// Whether the list is in bulk-select mode — while true, clicking the row
	// toggles selection instead of opening the draft into the form (a bigger,
	// easier click target than a small checkbox alone).
	isSelectMode: boolean;
	toggleSelection: (item: DraftListEntry) => void;
	currentDraft: (Partial<InvoiceFormValues> & Record<string, unknown>) | null;
	onSetCurrent: (draft: DraftListEntry) => void;
	unsavedInvoiceNumber: string;
	searchQuery?: string;
}

// Extracted from draft-list.component.jsx (was an inline function component
// in the same file) — a clean file-per-component split, no behavior change.
export default function DraftItem({
	draft,
	isSelected,
	isSelectMode,
	toggleSelection,
	currentDraft,
	onSetCurrent,
	unsavedInvoiceNumber,
	searchQuery = "",
}: DraftItemProps) {
	// currentDraft is the one chosen for editing

	function renderStatus() {
		let status;
		if (unsavedInvoiceNumber === draft?.invoice_number) {
			status = "editing";
		} else {
			if (
				draft?.hasOwnProperty("draft_bill_status") &&
				draft?.draft_bill_status === "draft"
			) {
				status = draft.draft_bill_status;
			} else {
				if (draft?.hasOwnProperty("draft_valid")) {
					status = "unsaved";
				} else {
					status = "new";
				}
			}
		}
		return <div className={`bill_status ${status}`}>{status}</div>;
	}

	return (
		<div
			className={`list-item-container ${
				(isSelectMode
					? isSelected
					: currentDraft?.invoice_number === draft.invoice_number) &&
				"selected"
			}`}
			onClick={() =>
				isSelectMode ? toggleSelection(draft) : onSetCurrent(draft)
			}
		>
			<div className="list-item-container-inner">
				{isSelectMode && (
					<div className={`draft-select ${isSelected && "selected"}`}>
						{isSelected && (
							<FaCheck
								style={{
									color: "#46BE46",
									fontSize: "12px",
								}}
							/>
						)}
					</div>
				)}

				<div className="list-details" style={{ flex: 1 }}>
					<div className="details-col">
						<div className="list-maintext">
							{draft?.buyer_name
								? highlightMatch(
										capitalizeWords(
											draft.buyer_name as string,
										),
										searchQuery,
									)
								: "[Customer Name]"}
						</div>
						<div className="list-subtext">
							Sales Date:{" "}
							<span>
								{draft?.sales_date
									? `${fromAdToBs(
											draft?.sales_date as string,
											"DD MMMM, YYYY",
										)} (${moment(
											draft?.sales_date as string,
										).format("DD MMMM, YYYY")})`
									: "[Sales Date]"}
							</span>
						</div>
					</div>
					<div className="details-divider"></div>
					<div className="details-row">
						<div className="list-smalltext">
							{draft?.draft_bill_status &&
								highlightMatch(
									`# ${draft?.invoice_number}`,
									searchQuery,
								)}
						</div>
						<div
							className="list-smalltext"
							style={{
								whiteSpace: "nowrap",
								overflow: "hidden",
								textOverflow: "ellipsis",
							}}
						>
							{draft?.updatedAt
								? `- ${moment(draft?.updatedAt as string).fromNow()}`
								: null}
						</div>
					</div>
				</div>
				<div className="list-details">
					{renderStatus()}
					{draft?.draft_valid && (
						<div className="valid-indicator">
							<FaCheck />
							<span>Validated</span>
						</div>
					)}
				</div>
			</div>
		</div>
	);
}
